import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import EraseAllButton from "../components/EraseAllButton";
import { api } from "../lib/api";

const STATUS_ICON = {
  open: "status-open",
  pending: "status-pending",
  accepted: "status-accepted",
  approved: "status-approved",
  accepted_by_customer: "status-approved", // legacy rows
};

const STATUS_LABEL = {
  open: "Open",
  pending: "Pending",
  accepted: "Accepted",
  approved: "Approved",
  accepted_by_customer: "Approved", // legacy rows
};

export default function QuotesHub() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("open"); // open | accepted
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/quotes", { params: { status: tab } })
      .then((r) => setQuotes(r.data))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/pos/dashboard")} className="label-eyebrow mb-6 inline-block" data-testid="quotes-back">← Back</button>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <h1 className="font-display text-4xl font-black">Quotes</h1>
          <EraseAllButton scope="quotes" label="Erase All Quotes" confirmText="every quote (open, pending, and accepted)" onDone={load} testid="quotes-erase-all" />
        </div>

        <div className="flex gap-2 mb-6 border-b" style={{ borderColor: "var(--ab-border)" }}>
          {["open", "accepted"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="px-5 py-3 text-xs uppercase tracking-widest font-bold"
              style={{
                borderBottom: tab === t ? "2px solid var(--ab-primary)" : "2px solid transparent",
                color: tab === t ? "var(--ab-text)" : "var(--ab-text-dim)",
              }}
              data-testid={`quotes-tab-${t}`}
            >
              {t === "open" ? "Open Quotes" : "Accepted Quotes"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ab-text-muted)" }}>
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : quotes.length === 0 ? (
          <div className="surface p-8 text-sm" style={{ color: "var(--ab-text-muted)" }} data-testid="quotes-empty">
            No {tab} quotes yet.
          </div>
        ) : (
          <div className="surface divide-y" style={{ borderColor: "var(--ab-border)" }}>
            {quotes.map((q) => {
              const total = q.items.reduce((s, it) => {
                const v = q.quote_type === "cash" ? it.cash_value : it.trade_value;
                return s + (v || 0);
              }, 0);
              return (
                <Link
                  key={q.id}
                  to={`/pos/quotes/${q.id}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-[var(--ab-surface-2)] transition-colors"
                  data-testid={`quote-row-${q.id}`}
                >
                  <span className={STATUS_ICON[q.status] || "status-open"} title={STATUS_LABEL[q.status]} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-base truncate">{q.customer_name}</div>
                    <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>
                      {q.quote_type.toUpperCase()} &middot; {q.items.length} item{q.items.length === 1 ? "" : "s"} &middot; {STATUS_LABEL[q.status]}
                    </div>
                  </div>
                  {q.awaiting_pickup && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ color: "var(--ab-warning)", border: "1px solid var(--ab-warning)" }} data-testid={`quote-awaiting-pickup-${q.id}`}>
                      Awaiting Pickup
                    </span>
                  )}
                  {!q.awaiting_pickup && q.dropoff_requested && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ color: "var(--ab-primary)", border: "1px solid var(--ab-primary)" }}>
                      Drop-Off Requested
                    </span>
                  )}
                  <div className="font-mono font-bold text-lg" style={{ color: "var(--ab-primary)" }}>${total.toFixed(2)}</div>
                  <ChevronRight size={16} style={{ color: "var(--ab-text-dim)" }} />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
